"use client";

import { useEffect } from "react";
import { Link } from "@/i18n/routing";
import { RefreshCw, Home, MessageCircle } from "lucide-react";
import { useTranslations } from "next-intl";

export default function LocaleError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const t = useTranslations("errors");

  useEffect(() => {
    console.error("Page error:", error);

    // Chunk-load errors happen when a customer has a stale HTML page
    // cached and a new deploy ships — the chunk URLs in the cached HTML
    // no longer exist. The fix is a hard reload so they pick up the
    // current chunk manifest. Match by message because Next.js wraps
    // the underlying ChunkLoadError in its own thrown error.
    const isChunkError =
      /Loading chunk \d+ failed|ChunkLoadError|Failed to fetch dynamically imported module/i.test(
        error.message || ""
      );
    if (isChunkError && typeof window !== "undefined") {
      // Time-windowed sentinel so a SECOND deploy mid-session also
      // auto-recovers. The old "set once forever" sentinel meant
      // Rivky lost her unsaved form on every deploy after the first
      // (3 chunk failures in a row at /admin/settings 2026-04-30).
      // 60s window prevents an infinite loop within a single deploy
      // while still re-arming for the next one.
      const sentinel = "__rw_chunk_reload_at";
      const last = parseInt(sessionStorage.getItem(sentinel) || "0", 10);
      if (Date.now() - last > 60_000) {
        sessionStorage.setItem(sentinel, String(Date.now()));
        window.location.reload();
        return;
      }
    }

    // Forward to /api/errors so route-boundary catches aren't silent.
    // window.onerror does not fire for React errors swallowed by error.tsx,
    // so we have to report from inside the boundary itself.
    try {
      const messageText = error.message || error.name || String(error) || "(empty error)";
      fetch("/api/errors", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: `Route error [${error.name || "Error"}]: ${messageText}`,
          stack: error.stack,
          digest: error.digest,
          url: typeof window !== "undefined" ? window.location.href : "",
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
          context: {
            kind: "route-error-boundary",
            errorName: error.name,
            errorDigest: error.digest,
            errorString: String(error),
            hasMessage: !!error.message,
          },
        }),
        keepalive: true,
      }).catch(() => {});
    } catch {}
  }, [error]);

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center px-6 py-20 text-center">
      {/* Decorative accent */}
      <div className="mb-10 h-px w-16 bg-brand-gold" />

      {/* Icon */}
      <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-brand-cream">
        <div className="h-8 w-8 rounded-full border-2 border-brand-gold/30 flex items-center justify-center">
          <span className="text-brand-gold text-lg font-display">!</span>
        </div>
      </div>

      <h1 className="font-display text-2xl tracking-tight text-brand-dark sm:text-3xl">
        {t("errorTitle")}
      </h1>

      <p className="mt-4 max-w-md text-sm leading-relaxed text-brand-muted">
        {t("errorMessage")}
      </p>

      {/* Actions */}
      <div className="mt-10 flex flex-col items-center gap-3 sm:flex-row sm:gap-4">
        <button
          onClick={reset}
          className="inline-flex items-center gap-2.5 bg-brand-dark px-8 py-3.5 text-xs font-medium uppercase tracking-[0.15em] text-white transition-all duration-300 hover:bg-brand-gold active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/40 focus-visible:ring-offset-2"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          {t("errorTryAgain")}
        </button>

        <Link
          href="/"
          className="inline-flex items-center gap-2.5 border border-brand-dark px-8 py-3.5 text-xs font-medium uppercase tracking-[0.15em] text-brand-dark transition-all duration-300 hover:bg-brand-dark hover:text-white active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/40 focus-visible:ring-offset-2"
        >
          <Home className="h-3.5 w-3.5" />
          {t("errorGoHome")}
        </Link>
      </div>

      {/* Contact support */}
      <div className="mt-12 max-w-sm rounded-sm border border-brand-dark/5 bg-brand-cream/30 px-6 py-5">
        <p className="text-xs leading-relaxed text-brand-muted">
          {t("errorContact")}
        </p>
        <Link
          href="/consultation"
          className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-brand-gold transition-colors duration-300 hover:text-brand-gold-dark"
        >
          <MessageCircle className="h-3.5 w-3.5" />
          {t("errorContactLink")}
        </Link>
      </div>

      {/* Decorative accent */}
      <div className="mt-10 h-px w-16 bg-brand-gold" />
    </div>
  );
}
